import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import * as d3 from 'd3'

import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'

import { calcMenuChartHeight } from '@aytm/app/stats/page/question-views/mn/components/menu-section/lib/constants'
import {
  DEFAULT_CONTAINER_WIDTH,
  EXPORT_OPTIONS,
  MENU_VIEW_MODES
} from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/constants'
import {
  getHeatmapData,
  hasOverlayData
} from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/overlayData'
import {
  drawOverlay,
  removeOverlay
} from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/render'
import styles from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/styles/MenuChart.module.scss'
import { MEDIA_INSET } from '@aytm/app/stats/page/question-views/components/lib/constants'

const MenuChart = ({ id, image, answer, sections, menuViewMode, isAnimated, onExport }) => {
  const wrapRef = useRef(null)
  const overlayRef = useRef(null)

  const [containerWidth, setContainerWidth] = useState(DEFAULT_CONTAINER_WIDTH)
  // pixels arrived (or failed) — the only thing the schema cannot tell upfront
  const [settled, setSettled] = useState(false)
  const [failed, setFailed] = useState(false)

  const imageUrl = image?.original?.url

  // the wrap exists from the first frame — one synchronous measure before paint
  useLayoutEffect(() => {
    const width = wrapRef.current?.clientWidth
    if (width) setContainerWidth(width)
  }, [])

  // schema dims make the display synchronous (D10) — only `settled` awaits the network
  const display = useMemo(() => {
    const { width, height } = image?.original || {}
    if (!width || !height) return null

    // scaled to the room inside the media inset the picture stands off the block edge by
    return { imageWidth: width, imageHeight: height, scale: Math.min(1, (containerWidth - 2 * MEDIA_INSET) / width) }
  }, [image, containerWidth])

  useEffect(() => {
    setSettled(!imageUrl) // no url → nothing to await, report ready immediately
    setFailed(false)
  }, [imageUrl])

  useEffect(() => {
    if (!settled || failed || !display || !overlayRef.current) return

    const container = overlayRef.current

    const heatmap = getHeatmapData(answer, menuViewMode)
    if (!hasOverlayData({ menuViewMode, heatmap, sections })) return

    drawOverlay(d3.select(container), {
      id,
      mode: menuViewMode,
      heatmap,
      sections,
      width: display.imageWidth,
      height: display.imageHeight,
      scale: display.scale
    })

    return () => removeOverlay(container)
  }, [id, display, answer, sections, menuViewMode, settled, failed])

  const handleExport = async (fileType, done) => {
    try {
      await onExport?.(fileType)
    } finally {
      done?.()
    }
  }

  if (!image?.original) return null

  const ready = settled && !failed && Boolean(display)

  return (
    <div className={styles.chart} ref={wrapRef}>
      {/* legacy drew the download icon inside the image onload — no button while loading or on error */}
      {ready && (
        <div className={styles.download}>
          <ExportDropdown options={EXPORT_OPTIONS} onExport={handleExport} />
        </div>
      )}

      {/* the one loading visual: the same skeleton the section shows pre-visibility, at the
          exact final image height — no jump when the pixels arrive (D10) */}
      {!settled && (
        <ChartPlaceholder
          height={calcMenuChartHeight(image, containerWidth - 2 * MEDIA_INSET)}
          isLoading
          isAnimated={isAnimated}
        />
      )}

      <div id={`mn_heatmap_${id}`} className={styles.heatmap}>
        {display && imageUrl && (
          <img
            src={imageUrl}
            alt=""
            onLoad={() => setSettled(true)}
            onError={() => {
              setFailed(true)
              setSettled(true)
            }}
            style={{
              width: Math.round(display.imageWidth * display.scale),
              height: Math.round(display.imageHeight * display.scale),
              display: ready ? 'block' : 'none'
            }}
          />
        )}

        <div ref={overlayRef} />
      </div>
    </div>
  )
}

MenuChart.displayName = 'MenuChart'

MenuChart.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  image: PropTypes.shape({
    original: PropTypes.shape({
      url: PropTypes.string,
      path: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number
    })
  }),
  answer: answerPropType,
  sections: PropTypes.array,
  menuViewMode: PropTypes.oneOf(Object.values(MENU_VIEW_MODES)),
  isAnimated: PropTypes.bool,
  onExport: PropTypes.func
}

export default MenuChart
